package com.crowpay.utils

import com.lightningkite.kiteui.AppScope
import com.lightningkite.kiteui.Console
import com.lightningkite.kiteui.ConsoleRoot
import com.lightningkite.kiteui.reactive.Listenable
import com.lightningkite.kiteui.reactive.invokeAllSafe
import com.lightningkite.kiteui.reactive.onRemove
import com.lightningkite.kiteui.views.RView
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.time.Duration

// Basically JumpTo but handles requesting focus instead of scrolling into view.
object RequestFocus {
    private val debug: Console? = null//ConsoleRoot.tag("RequestFocus")

    data class Target(val view: RView, val delayBeforeFocus: Duration? = null, val action: ()->Unit) {
        fun focus() {
            debug?.info("going to target")
            action()
            if (delayBeforeFocus != null) AppScope.launch {
                debug?.info("Delaying scroll")
                delay(delayBeforeFocus)
                debug?.info("focusing")
                view.requestFocus()
            }
            else {
                debug?.info("focusing")
                view.requestFocus()
            }
        }
    }

    sealed interface Handler : Listenable

    class TargetHandler(val name: String) : Listenable, Handler {
        private val listeners = ArrayList<()->Unit>()
        override fun addListener(listener: () -> Unit): () -> Unit {
            debug?.info("Adding listener for $name")
            listeners.add(listener)
            if (awaitingTarget) {
                debug?.info("$name Calling listener immediately")
                listener()
            }
            return {
                debug?.info("Removing listener for $name")
                listeners.remove(listener)
            }
        }

        private var target: Target? = null
        private var awaitingTarget = false

        fun setTarget(view: RView, delayBeforeFocus: Duration? = null, onFocus: () -> Unit = {}) {
            debug?.info("$name Adding target")
            target = Target(view, delayBeforeFocus, onFocus)
            view.onRemove {
                if (view === target?.view) {
                    debug?.info("$name removing target")
                    target = null
                }
            }
            if (awaitingTarget) {
                debug?.info("$name Fulfilling awaiting target")
                target?.focus()
                awaitingTarget = false
            }
        }

        operator fun invoke() {
            listeners.invokeAllSafe()
            target
                ?.also { debug?.info("Target found for $name") }
                ?.focus()
                ?: run {
                    debug?.info("No target found for $name")
                    awaitingTarget = true
                }
        }
    }
}